import React, { useState, useEffect } from 'react';

const GPSTracking = ({ onBack, user }) => {
  const [gpsDevices, setGpsDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [deviceDetails, setDeviceDetails] = useState(null);
  const [deviceStatus, setDeviceStatus] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [actionType, setActionType] = useState(null); // 'forward'
  const [forwardPrefilled, setForwardPrefilled] = useState(false);
  const [editingDetails, setEditingDetails] = useState(false);
  const [editedDetails, setEditedDetails] = useState({
    dodatna_oprema: '',
    napomena: ''
  });
  const [formData, setFormData] = useState({
    forward_destination: 'office', // 'office' | 'employee'
    to_worker_id: '',
    dodatna_oprema: '',
    napomena: '',
    ostecenje: ''
  });
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Use relative URLs to go through Nginx proxy
  // In production, this should be empty to use relative URLs
  // For local dev, set REACT_APP_PYTHON_API_URL=http://localhost:8005
  const API_URL = process.env.REACT_APP_PYTHON_API_URL || '';
  
  // Debug: Log API URL configuration
  if (process.env.NODE_ENV === 'development') {
    console.log('GPS Tracking API URL:', API_URL || '(using relative URLs)');
  }

  useEffect(() => {
    fetchGPSDevices();
    fetchEmployees();
  }, []);

  useEffect(() => {
    if (selectedDevice) {
      console.log('Selected device changed:', selectedDevice);
      // Reset deviceDetails when selecting a new device
      setDeviceDetails(null);
      setEditingDetails(false);
      setForwardPrefilled(false);
      fetchDeviceDetails(selectedDevice.gps_id);
      fetchDeviceStatus(selectedDevice.gps_id);
    } else {
      // Reset when no device is selected
      setDeviceDetails(null);
      setEditingDetails(false);
      setForwardPrefilled(false);
    }
  }, [selectedDevice]);

  // Prefill forward form fields from device details (from DB) when Proslijedi form is open
  useEffect(() => {
    if (actionType !== 'forward') {
      setForwardPrefilled(false);
      return;
    }
    if (!deviceDetails || forwardPrefilled) return;

    setFormData((prev) => ({
      ...prev,
      dodatna_oprema: deviceDetails?.detalji?.dodatna_oprema ?? '',
      napomena: deviceDetails?.detalji?.napomena ?? '',
      ostecenje: deviceDetails?.detalji?.ostecenje ?? ''
    }));
    setForwardPrefilled(true);
  }, [actionType, deviceDetails, forwardPrefilled]);

  const openForwardForm = () => {
    setError('');
    setSuccess('');
    setActionType('forward');
    setForwardPrefilled(false);
    if (selectedDevice?.gps_id) {
      fetchDeviceDetails(selectedDevice.gps_id);
    }
    setFormData((prev) => ({
      ...prev,
      forward_destination: 'office',
      to_worker_id: '',
      dodatna_oprema: deviceDetails?.detalji?.dodatna_oprema ?? prev.dodatna_oprema ?? '',
      napomena: deviceDetails?.detalji?.napomena ?? prev.napomena ?? '',
      ostecenje: deviceDetails?.detalji?.ostecenje ?? prev.ostecenje ?? ''
    }));
  };

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchGPSDevices = async () => {
    try {
      setLoading(true);
      const zaposlenikId = user?.zaposlenik_id;
      const qs = zaposlenikId ? `?zaposlenik_id=${encodeURIComponent(zaposlenikId)}` : '';
      const response = await fetch(`${API_URL}/gps${qs}`);
      if (!response.ok) throw new Error('Failed to fetch GPS devices');
      const data = await response.json();
      setGpsDevices(data);
    } catch (err) {
      setError('Greška pri učitavanju GPS uređaja: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await fetch(`${API_URL}/zaposlenici-aktivni`);
      if (!response.ok) throw new Error('Failed to fetch employees');
      const data = await response.json();
      // Sort by Prezime, then Ime
      const sorted = data.sort((a, b) => {
        if (a.prezime !== b.prezime) {
          return (a.prezime || '').localeCompare(b.prezime || '', 'hr');
        }
        return (a.ime || '').localeCompare(b.ime || '', 'hr');
      });
      setEmployees(sorted);
    } catch (err) {
      console.error('Error fetching employees:', err);
    }
  };

  const fetchDeviceDetails = async (gpsId) => {
    if (!gpsId) {
      console.error('fetchDeviceDetails called without gpsId');
      return;
    }
    
    try {
      const url = `${API_URL}/gps/${gpsId}`;
      console.log('Fetching GPS details:', { gpsId, url, API_URL });
      
      const response = await fetch(url);
      console.log('Response received:', { status: response.status, statusText: response.statusText, ok: response.ok });
      
      if (!response.ok) {
        let errorText;
        try {
          errorText = await response.text();
        } catch {
          errorText = `HTTP ${response.status} ${response.statusText}`;
        }
        console.error('Error response:', errorText);
        throw new Error(`Failed to fetch device details: ${response.status} - ${errorText}`);
      }
      
      const data = await response.json();
      console.log('GPS details received:', data);
      
      if (!data) {
        throw new Error('No data received from API');
      }
      
      setDeviceDetails(data);
      setEditedDetails({
        dodatna_oprema: data.detalji?.dodatna_oprema || '',
        napomena: data.detalji?.napomena || '',
        ostecenje: data.detalji?.ostecenje || ''
      });
    } catch (err) {
      console.error('Error fetching device details:', err);
      const errorMsg = err.message || 'Nepoznata greška';
      setError(`Greška pri učitavanju detalja: ${errorMsg}`);
      // Don't set deviceDetails to null here - keep previous value if any
    }
  };

  const fetchDeviceStatus = async (gpsId) => {
    try {
      const response = await fetch(`${API_URL}/gps/${gpsId}/status`);
	     if (response.status === 404) {
      setDeviceStatus(null);
      return;
    }

      if (!response.ok) throw new Error('Failed to fetch device status');
      const data = await response.json();
      setDeviceStatus(data);
    } catch (err) {
      setError('Greška pri učitavanju statusa: ' + err.message);
    }
  };


  const handleForward = async () => {
    if (!selectedDevice) return;

    const me = user?.zaposlenik_id;
    if (!me) {
      setError('Nedostaje prijavljeni korisnik (zaposlenik_id).');
      return;
    }

    const dest = formData.forward_destination;
    if (dest !== 'office' && dest !== 'employee') {
      setError('Molimo odaberite odredište (Ured ili Drugom zaposleniku).');
      return;
    }

    if (dest === 'employee') {
      if (!formData.to_worker_id) {
        setError('Molimo odaberite zaposlenika.');
        return;
      }
      if (parseInt(formData.to_worker_id, 10) === parseInt(me, 10)) {
        setError('Ne možete proslijediti sami sebi.');
        return;
      }
    }

    try {
      setLoading(true);
      setError('');

      if (dest === 'office') {
        const response = await fetch(`${API_URL}/gps/${selectedDevice.gps_id}/return`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            returning_worker_id: parseInt(me, 10),
            od_zaposlenik_id: parseInt(me, 10),
            dodatna_oprema: formData.dodatna_oprema,
            napomena: formData.napomena,
            ostecenje: formData.ostecenje
          })
        });

        const data = await response.json();
        if (!response.ok) throw new Error(data.detail || 'Greška pri vraćanju u ured');

        setSuccess('GPS uređaj uspješno vraćen u ured.');
        setActionType(null);
        setFormData({
          forward_destination: 'office',
          to_worker_id: '',
          dodatna_oprema: '',
          napomena: '',
          ostecenje: ''
        });
        setSelectedDevice(null);
        setDeviceDetails(null);
        setDeviceStatus(null);
        setEditedDetails({ dodatna_oprema: '', napomena: '' });
        fetchGPSDevices();
        setTimeout(() => setSuccess(''), 5000);
        return;
      }

      // dest === 'employee'
      const response = await fetch(`${API_URL}/gps/${selectedDevice.gps_id}/transfer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to_worker_id: parseInt(formData.to_worker_id, 10),
          od_zaposlenik_id: parseInt(me, 10),
          issuing_worker_id: parseInt(me, 10),
          dodatna_oprema: formData.dodatna_oprema,
          napomena: formData.napomena,
          ostecenje: formData.ostecenje
        })
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Greška pri prosljeđivanju');

      setSuccess('GPS uređaj uspješno proslijeđen zaposleniku.');
      setActionType(null);
      setFormData({
        forward_destination: 'office',
        to_worker_id: '',
        dodatna_oprema: '',
        napomena: '',
        ostecenje: ''
      });
      setSelectedDevice(null);
      setDeviceDetails(null);
      setDeviceStatus(null);
      setEditedDetails({ dodatna_oprema: '', napomena: '' });
      fetchGPSDevices();
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };


  const handleSaveDetails = async () => {
    if (!selectedDevice) return;
    
    try {
      setLoading(true);
      setError('');
      const response = await fetch(`${API_URL}/gps/${selectedDevice.gps_id}/details`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          dodatna_oprema: editedDetails.dodatna_oprema,
          napomena: editedDetails.napomena
        })
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || 'Greška pri spremanju');
      }

      setSuccess('Detalji uspješno spremljeni');
      setEditingDetails(false);
      fetchDeviceDetails(selectedDevice.gps_id);
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingDetails(false);
    if (deviceDetails) {
      setEditedDetails({
        dodatna_oprema: deviceDetails.detalji?.dodatna_oprema || '',
        napomena: deviceDetails.detalji?.napomena || ''
      });
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #CAE0BC 0%, #EAFAEA 100%)',
      padding: isMobile ? '10px' : '20px',
      fontFamily: 'Arial, sans-serif'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        marginBottom: isMobile ? '15px' : '20px',
        gap: isMobile ? '10px' : '15px'
      }}>
        <button
          onClick={onBack}
          style={{
            background: '#6C8B58',
            color: 'white',
            border: 'none',
            padding: isMobile ? '8px 16px' : '10px 20px',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: isMobile ? '0.9rem' : '1rem',
            fontWeight: 'bold'
          }}
        >
          ← Nazad
        </button>
        <h1 style={{
          margin: 0,
          fontSize: isMobile ? '1.3rem' : '1.8rem',
          color: '#6C8B58',
          fontWeight: 'bold'
        }}>GPS Praćenje</h1>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div style={{
          background: '#ffebee',
          color: '#c62828',
          padding: '15px',
          borderRadius: '8px',
          marginBottom: '20px',
          border: '1px solid #c62828'
        }}>
          {error}
        </div>
      )}
      {success && (
        <div style={{
          background: '#e8f5e9',
          color: '#2e7d32',
          padding: '15px',
          borderRadius: '8px',
          marginBottom: '20px',
          border: '1px solid #2e7d32'
        }}>
          {success}
        </div>
      )}

      <div style={{ 
        display: 'flex', 
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? '15px' : '20px', 
        flexWrap: 'wrap' 
      }}>
        {/* Left Panel - Device List */}
        <div style={{
          flex: isMobile ? 'none' : '1',
          width: isMobile ? '100%' : 'auto',
          minWidth: isMobile ? 'auto' : '300px',
          background: 'white',
          borderRadius: '12px',
          padding: isMobile ? '15px' : '20px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
        }}>
          <h2 style={{ 
            marginTop: 0, 
            color: '#6C8B58',
            fontSize: isMobile ? '1.2rem' : '1.5rem'
          }}>GPS Uređaji</h2>
          {loading && !selectedDevice ? (
            <p>Učitavanje...</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {gpsDevices.map(device => (
                <button
                  key={device.gps_id}
                  onClick={() => setSelectedDevice(device)}
                  style={{
                    padding: '15px',
                    border: '2px solid',
                    borderColor: selectedDevice?.gps_id === device.gps_id ? '#6C8B58' : '#e1e5e9',
                    borderRadius: '8px',
                    background: selectedDevice?.gps_id === device.gps_id ? '#f1f8f4' : 'white',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'all 0.3s ease'
                  }}
                >
                  <div style={{ fontWeight: 'bold', color: '#333' }}>
                    {device.evidencijski_broj}
                  </div>
                  <div style={{ fontSize: '0.9rem', color: '#666' }}>
                    {device.GNSS_model}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Right Panel - Device Details & Actions */}
        {selectedDevice && (
          <div style={{
            flex: isMobile ? 'none' : '2',
            width: isMobile ? '100%' : 'auto',
            minWidth: isMobile ? 'auto' : '400px',
            background: 'white',
            borderRadius: '12px',
            padding: isMobile ? '15px' : '20px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
          }}>
            <h2 style={{ 
              marginTop: 0, 
              color: '#6C8B58',
              fontSize: isMobile ? '1.2rem' : '1.5rem'
            }}>
              {selectedDevice.evidencijski_broj}
            </h2>

            {/* Debug info - remove in production */}
            {process.env.NODE_ENV === 'development' && (
              <div style={{ padding: '10px', background: '#fff3cd', borderRadius: '4px', marginBottom: '10px', fontSize: '0.8rem' }}>
                <strong>Debug:</strong> selectedDevice={selectedDevice?.gps_id}, deviceDetails={deviceDetails ? 'loaded' : 'null'}
              </div>
            )}

            {/* Loading indicator for details */}
            {selectedDevice && !deviceDetails && !error && (
              <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                Učitavanje detalja...
              </div>
            )}

            {/* Error message if details failed to load */}
            {selectedDevice && error && (
              <div style={{ padding: '15px', background: '#ffebee', color: '#c62828', borderRadius: '8px', marginBottom: '20px' }}>
                <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>Greška:</div>
                <div>{error}</div>
                <button
                  onClick={() => {
                    setError('');
                    fetchDeviceDetails(selectedDevice.gps_id);
                  }}
                  style={{
                    marginTop: '10px',
                    padding: '8px 16px',
                    background: '#c62828',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Pokušaj ponovno
                </button>
              </div>
            )}

            {/* GPS Device Details - Show if we have details OR show basic info from selectedDevice */}
            {(deviceDetails || selectedDevice) && (
              <div style={{
                padding: '20px',
                background: '#f8f9fa',
                borderRadius: '8px',
                marginBottom: '20px',
                border: '1px solid #e1e5e9'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                  <h3 style={{ 
                    margin: 0, 
                    color: '#6C8B58',
                    fontSize: isMobile ? '1rem' : '1.2rem'
                  }}>Detalji Uređaja</h3>
                  {!editingDetails && deviceDetails && (
                    <button
                      onClick={() => setEditingDetails(true)}
                      style={{
                        padding: '8px 16px',
                        background: '#6C8B58',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '0.9rem'
                      }}
                    >
                      Uredi
                    </button>
                  )}
                  {!deviceDetails && (
                    <button
                      onClick={() => fetchDeviceDetails(selectedDevice.gps_id)}
                      style={{
                        padding: '8px 16px',
                        background: '#6C8B58',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '0.9rem'
                      }}
                    >
                      Učitaj Detalje
                    </button>
                  )}
                </div>
                
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', 
                  gap: isMobile ? '10px' : '15px' 
                }}>
                  <div>
                    <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', color: '#666' }}>
                      Evidencijski Broj:
                    </label>
                    <div style={{ padding: '8px', background: 'white', borderRadius: '4px', border: '1px solid #ddd' }}>
                      {deviceDetails?.evidencijski_broj || selectedDevice?.evidencijski_broj || 'N/A'}
                    </div>
                  </div>
                  
                  <div>
                    <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', color: '#666' }}>
                      GNSS Model:
                    </label>
                    <div style={{ padding: '8px', background: 'white', borderRadius: '4px', border: '1px solid #ddd' }}>
                      {deviceDetails?.GNSS_model || selectedDevice?.GNSS_model || 'N/A'}
                    </div>
                  </div>
                  
                  <div>
                    <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', color: '#666' }}>
                      GNSS Serijski Broj:
                    </label>
                    <div style={{ padding: '8px', background: 'white', borderRadius: '4px', border: '1px solid #ddd' }}>
                      {deviceDetails?.GNSS_serijski_broj || selectedDevice?.GNSS_serijski_broj || 'N/A'}
                    </div>
                  </div>
                  
                  <div>
                    <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', color: '#666' }}>
                      Model Kontrolera:
                    </label>
                    <div style={{ padding: '8px', background: 'white', borderRadius: '4px', border: '1px solid #ddd' }}>
                      {deviceDetails?.model_kontrolera || selectedDevice?.model_kontrolera || 'N/A'}
                    </div>
                  </div>
                  
                  <div>
                    <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', color: '#666' }}>
                      Distomat:
                    </label>
                    <div style={{ padding: '8px', background: 'white', borderRadius: '4px', border: '1px solid #ddd' }}>
                      {deviceDetails?.detalji?.distomat || 'N/A'}
                    </div>
                  </div>
                  
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', color: '#666' }}>
                      Dodatna Oprema:
                    </label>
                    {editingDetails && deviceDetails ? (
                      <input
                        type="text"
                        value={editedDetails.dodatna_oprema}
                        onChange={(e) => setEditedDetails({ ...editedDetails, dodatna_oprema: e.target.value })}
                        style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                      />
                    ) : (
                      <div style={{ padding: '8px', background: 'white', borderRadius: '4px', border: '1px solid #ddd', minHeight: '20px' }}>
                        {deviceDetails?.detalji?.dodatna_oprema || 'N/A'}
                      </div>
                    )}
                  </div>
                  
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', color: '#666' }}>
                      Napomena:
                    </label>
                    {editingDetails && deviceDetails ? (
                      <textarea
                        value={editedDetails.napomena}
                        onChange={(e) => setEditedDetails({ ...editedDetails, napomena: e.target.value })}
                        style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd', minHeight: '80px', resize: 'vertical' }}
                      />
                    ) : (
                      <div style={{ padding: '8px', background: 'white', borderRadius: '4px', border: '1px solid #ddd', minHeight: '80px', whiteSpace: 'pre-wrap' }}>
                        {deviceDetails?.detalji?.napomena || 'N/A'}
                      </div>
                    )}
                  </div>
                </div>
                
                {editingDetails && (
                  <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
                    <button
                      onClick={handleSaveDetails}
                      disabled={loading}
                      style={{
                        padding: '10px 20px',
                        background: '#6C8B58',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontWeight: 'bold'
                      }}
                    >
                      {loading ? 'Spremanje...' : 'Spremi'}
                    </button>
                    <button
                      onClick={handleCancelEdit}
                      disabled={loading}
                      style={{
                        padding: '10px 20px',
                        background: '#ccc',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer'
                      }}
                    >
                      Odustani
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Current Status */}
            {deviceStatus && (
              <div style={{
                padding: '15px',
                background: deviceStatus.status === 'at_office' ? '#e8f5e9' : 
                           deviceStatus.status === 'available_for_field' ? '#e3f2fd' : '#fff3e0',
                borderRadius: '8px',
                marginBottom: '20px',
                border: `2px solid ${
                  deviceStatus.status === 'at_office' ? '#4caf50' : 
                  deviceStatus.status === 'available_for_field' ? '#2196f3' : '#ff9800'
                }`
              }}>
                <div style={{ fontWeight: 'bold', marginBottom: '10px' }}>
                  Status: {
                    deviceStatus.status === 'at_office' ? 'U uredu' : 
                    deviceStatus.status === 'available_for_field' ? 'Dostupan za prijenos' : 
                    'Na terenu'
                  }
                </div>
                {deviceStatus.current_holder_name && (
                  <div>
                    Trenutno kod: <strong>{deviceStatus.current_holder_name}</strong>
                  </div>
                )}
                {deviceStatus.last_returned_by_name && deviceStatus.status === 'available_for_field' && (
                  <div>
                    Vratio: <strong>{deviceStatus.last_returned_by_name}</strong>
                  </div>
                )}
                {deviceStatus.issued_at && (
                  <div style={{ fontSize: '0.9rem', color: '#666', marginTop: '5px' }}>
                    Izdano: {new Date(deviceStatus.issued_at).toLocaleString('hr-HR')}
                  </div>
                )}
                {deviceStatus.last_returned_at && deviceStatus.status === 'available_for_field' && (
                  <div style={{ fontSize: '0.9rem', color: '#666', marginTop: '5px' }}>
                    Vraćeno: {new Date(deviceStatus.last_returned_at).toLocaleString('hr-HR')}
                  </div>
                )}
              </div>
            )}

            {/* Action */}
            <div style={{ 
              display: 'flex', 
              flexDirection: isMobile ? 'column' : 'row',
              gap: isMobile ? '10px' : '10px', 
              marginBottom: isMobile ? '15px' : '20px', 
              flexWrap: 'wrap' 
            }}>
              <button
                onClick={openForwardForm}
                disabled={!(deviceStatus?.stanje_ured?.toString().toLowerCase() === 'vani' && (deviceStatus?.status_id === 1 || deviceStatus?.status_id === 2))}
                style={{
                  padding: isMobile ? '14px 20px' : '12px 20px',
                  width: isMobile ? '100%' : 'auto',
                  background: (deviceStatus?.stanje_ured?.toString().toLowerCase() === 'vani' && (deviceStatus?.status_id === 1 || deviceStatus?.status_id === 2)) ? '#6C8B58' : '#ccc',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: (deviceStatus?.stanje_ured?.toString().toLowerCase() === 'vani' && (deviceStatus?.status_id === 1 || deviceStatus?.status_id === 2)) ? 'pointer' : 'not-allowed',
                  fontWeight: 'bold',
                  fontSize: isMobile ? '1rem' : '0.95rem'
                }}
              >
                Proslijedi
              </button>
            </div>
            {deviceStatus?.status === 'at_office' && (
              <div style={{
                padding: '10px',
                background: '#fff9c4',
                borderRadius: '8px',
                marginBottom: '20px',
                fontSize: '0.9rem',
                color: '#666'
              }}>
                ℹ️ GPS je u uredu. Za izdavanje iz ureda koristite desktop aplikaciju.
              </div>
            )}

            {/* Action Form */}
            {actionType === 'forward' && (
              <div style={{
                padding: '20px',
                background: '#f8f9fa',
                borderRadius: '8px',
                marginBottom: '20px'
              }}>
                <h3 style={{ 
                  marginTop: 0,
                  fontSize: isMobile ? '1rem' : '1.2rem'
                }}>Proslijedi</h3>
                <div style={{
                  padding: '10px',
                  background: '#e3f2fd',
                  borderRadius: '8px',
                  marginBottom: '15px',
                  fontSize: '0.9rem',
                  color: '#666'
                }}>
                  ℹ️ Odaberi želite li proslijediti u ured ili drugom zaposleniku. U oba slučaja možete ažurirati napomenu, dodatnu opremu i oštećenje.
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                  <div>
                    <label>Odredište:</label>
                    <select
                      value={formData.forward_destination}
                      onChange={(e) => setFormData({ ...formData, forward_destination: e.target.value })}
                      style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                    >
                      <option value="office">Ured</option>
                      <option value="employee">Drugom zaposleniku</option>
                    </select>
                  </div>

                  {formData.forward_destination === 'employee' && (
                    <div>
                      <label>Zaposlenik:</label>
                      <select
                        value={formData.to_worker_id}
                        onChange={(e) => setFormData({ ...formData, to_worker_id: e.target.value })}
                        style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                      >
                        <option value="">Odaberi zaposlenika...</option>
                        {employees
                          .filter(emp => emp.zaposlenik_id !== user?.zaposlenik_id)
                          .map(emp => (
                            <option key={emp.zaposlenik_id} value={emp.zaposlenik_id}>
                              {emp.prezime} {emp.ime}
                            </option>
                          ))}
                      </select>
                    </div>
                  )}

                  <div>
                    <label>Dodatna Oprema:</label>
                    <input
                      type="text"
                      value={formData.dodatna_oprema}
                      onChange={(e) => setFormData({ ...formData, dodatna_oprema: e.target.value })}
                      style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                    />
                  </div>
                  <div>
                    <label>Oštećenje:</label>
                    <textarea
                      value={formData.ostecenje}
                      onChange={(e) => setFormData({ ...formData, ostecenje: e.target.value })}
                      style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd', minHeight: '80px' }}
                    />
                  </div>
                  <div>
                    <label>Napomena:</label>
                    <textarea
                      value={formData.napomena}
                      onChange={(e) => setFormData({ ...formData, napomena: e.target.value })}
                      style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd', minHeight: '80px' }}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                      onClick={handleForward}
                      disabled={loading}
                      style={{
                        padding: '10px 20px',
                        background: '#6C8B58',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontWeight: 'bold'
                      }}
                    >
                      {loading ? 'Spremanje...' : 'Potvrdi'}
                    </button>
                    <button
                      onClick={() => setActionType(null)}
                      style={{
                        padding: '10px 20px',
                        background: '#ccc',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer'
                      }}
                    >
                      Odustani
                    </button>
                  </div>
                </div>
              </div>
            )}

          </div>
        )}
      </div>
    </div>
  );
};

export default GPSTracking;
